import dayjs from "dayjs";
import { getSeasonPhase, getSeasonWorkoutGuidance } from "../lib/seasonProfile";
import { buildSwotPriorityPayload } from "../lib/swotPriorityRouter";

// --- Helper: Safe JSON Parser ---
const safeParse = (input, fallback = {}) => {
  if (!input) return fallback;
  if (typeof input === "object") return input;
  try {
    const parsed = JSON.parse(input);
    return typeof parsed === "string" ? JSON.parse(parsed) : parsed;
  } catch (e) {
    return fallback;
  }
};

const normalizeSwotAction = (action) => {
  if (typeof action === "string") return { text: action.trim(), completed: false };
  if (action && typeof action === "object") {
    return {
      text: String(action.text || action.item || action.action || "").trim(),
      completed: Boolean(action.completed),
    };
  }
  return { text: "", completed: false };
};

const getSwotPriorityFocus = (selectedSwot) => {
  const explicitPriorities = Array.isArray(selectedSwot?.season_priority_focus_items)
    ? selectedSwot.season_priority_focus_items
    : [];
  if (explicitPriorities.length > 0) return explicitPriorities;

  const categories = ["strengths", "weaknesses", "opportunities", "threats"];
  const candidates = categories.flatMap((category) =>
    (Array.isArray(selectedSwot?.[category]) ? selectedSwot[category] : [])
      .filter((entry) => Boolean(entry?.priority) && !Boolean(entry?.completed) && String(entry?.item || "").trim() !== "")
      .map((entry) => ({
        item: String(entry.item || "").trim(),
        priorityRank: Number.isFinite(Number(entry?.priorityRank)) ? Number(entry.priorityRank) : 999,
      }))
  );

  return candidates
    .sort((a, b) => a.priorityRank - b.priorityRank)
    .slice(0, 3)
    .map((entry) => entry.item);
};

const getSwotSeasonPriorities = (selectedSwot) => {
  if (Array.isArray(selectedSwot?.season_priorities)) {
    return selectedSwot.season_priorities.filter((entry) => !entry?.completed);
  }
  const categories = ["strengths", "weaknesses", "opportunities", "threats"];
  return categories.flatMap((category) =>
    (Array.isArray(selectedSwot?.[category]) ? selectedSwot[category] : [])
      .filter((entry) => Boolean(entry?.priority) && !Boolean(entry?.completed))
      .map((entry) => ({
        id: entry?.id || null,
        category,
        section: entry?.section || "General",
        item: entry?.item || "",
        priorityRank: Number.isFinite(Number(entry?.priorityRank)) ? Number(entry.priorityRank) : null,
        completed: Boolean(entry?.completed),
        actions: (Array.isArray(entry?.actions) ? entry.actions : [])
          .map(normalizeSwotAction)
          .filter((action) => action.text !== ""),
      }))
  );
};

/**
 * PHASE 1: Ensure a Monthly Overview Exists
 * (Kept your logic to fetch or create the container)
 */
const ensureMonthlyOverview = async (supabase, userId, program, today, focusAreas) => {
  const { data: existing } = await supabase
    .from("user_monthly_skill_overviews")
    .select("*")
    .eq("user_id", userId)
    .eq("program_id", program.id)
    .lte("start_date", today)
    .gte("end_date", today)
    .limit(1);

  if (existing && existing.length > 0) {
    const current = existing[0];
    const parsedExisting = safeParse(current.focus_areas, []);
    if (JSON.stringify(parsedExisting) !== JSON.stringify(focusAreas || [])) {
      const { data: updated, error: updateError } = await supabase
        .from("user_monthly_skill_overviews")
        .update({ focus_areas: JSON.stringify(focusAreas || []) })
        .eq("id", current.id)
        .select("*")
        .single();
      if (updateError) throw new Error(`Monthly Update Failed: ${updateError.message}`);
      return updated;
    }
    return current;
  }

  // If missing, fetch scores and create new
  // (Assuming your creation logic is correct)
  const { data: skillScores } = await supabase
    .from("user_skill_scores")
    .select(`user_score, tag, program_goal_skill_scores!fk_user_skill_scores_program_goal_skill_score(*)`)
    .eq("athlete_id", userId)
    .eq("program_id", program.id);

  const { data: programSkills } = await supabase
    .from("program_goal_skill_scores")
    .select("target_score, skill_name")
    .eq("program_id", program.id);

  const userSkillScores = {};
  skillScores?.forEach((s) => {
    const skillName = s.program_goal_skill_scores?.skill_name || s.tag;
    userSkillScores[skillName] = s.user_score;
  });

  const programSkillScores = {};
  programSkills?.forEach((s) => {
    programSkillScores[s.skill_name] = s.target_score;
  });

  const newOverview = {
    start_date: today,
    end_date: dayjs().add(1, "month").format("YYYY-MM-DD"),
    program_id: program.id,
    modeled_athlete: program.modeled_athlete || "Unknown",
    program_skill_scores: JSON.stringify(programSkillScores),
    user_skill_scores: JSON.stringify(userSkillScores),
    user_id: userId,
    focus_areas: JSON.stringify(focusAreas || []),
  };

  const { data: inserted, error: insertError } = await supabase
    .from("user_monthly_skill_overviews")
    .insert([newOverview])
    .select()
    .single();

  if (insertError) throw new Error(`Monthly Creation Failed: ${insertError.message}`);
  
  return inserted;
};

/**
 * PHASE 2: Ensure a Weekly Plan Exists
 * FIX: This is now "Read Only". It will NOT call the generator.
 */
const ensureWeeklyPlan = async (supabase, monthlyOverview, today) => {
  // We simply look for the week that covers 'today'
  // logic: start_date <= today AND end_date >= today
  const { data: existingWeeks, error } = await supabase
    .from("weekly_skill_workouts")
    .select("*")
    .eq("plan_id", monthlyOverview.id)
    .lte("start_date", today)
    .gte("end_date", today)
    .limit(1);

  if (error) throw new Error(`Error fetching weekly plan: ${error.message}`);
  
  if (!existingWeeks || existingWeeks.length === 0) {
    throw new Error("Weekly Plan not found. Please wait for the monthly plan to finish generating.");
  }

  return existingWeeks[0];
};

/**
 * MAIN FUNCTION: Generate Daily Workout
 */
export const generateDailyWorkout = async ({ 
  userId, 
  userGrade, 
  selectedSwot,
  baseballFocusDomain,
  program, 
  monthlyOverview, 
  workoutLength = 60, 
  supabase, 
  setStatusMessage = () => {} 
}) => {
  
  setStatusMessage("Checking your program...");
  const today = dayjs().format("YYYY-MM-DD");
  const seasonPhase = getSeasonPhase(program);
  const seasonGuidance = getSeasonWorkoutGuidance(seasonPhase);
  const effectiveWorkoutLength = seasonPhase === "in_season"
    ? Math.min(Number(workoutLength) || 60, seasonGuidance.recommendedWorkoutLength)
    : Number(workoutLength) || seasonGuidance.recommendedWorkoutLength;
  const priorityPromptDirectives = {
    priority_execution_order: "Use Priority #1 as primary until complete, then Priority #2, then Priority #3.",
    session_focus_rotation: "Do not skip ahead in the priority queue.",
    allocation_model: "Allocate approximately 50% of session to active priority queue, 25% to strengths reinforcement, 25% to weaknesses correction.",
    spiral_curriculum: "Maintain spiral curriculum by revisiting prior concepts while increasing difficulty and context each week.",
    opening_drill_rule: "The first drills of each workout must directly target the workout's main priority focus.",
    weekly_focus_pattern: "When one priority is completed, promote the next priority immediately.",
    completion_progression_rule: "When an item or priority is marked completed, stop using it as workout focus and automatically continue with the next active item/priority."
  };

  try {
    const swotPriorityPayload = buildSwotPriorityPayload(selectedSwot, {
      sport: program?.sport,
      baseballFocusDomain
    });
    const swotPriorityFocus = Array.isArray(swotPriorityPayload?.season_priority_focus_items)
      ? swotPriorityPayload.season_priority_focus_items
      : getSwotPriorityFocus(selectedSwot);
    const fallbackProgramFocus = safeParse(program?.focus_areas, []);
    const effectiveFocusAreas = swotPriorityFocus.length > 0 ? swotPriorityFocus : fallbackProgramFocus;
    const seasonPriorities = Array.isArray(swotPriorityPayload?.skill_priorities)
      ? swotPriorityPayload.skill_priorities
      : getSwotSeasonPriorities(selectedSwot);

    // --- STEP 1: Monthly Overview ---
    let activeMonthly = monthlyOverview;
    if (!activeMonthly) {
        activeMonthly = await ensureMonthlyOverview(supabase, userId, program, today, effectiveFocusAreas);
    }
    
    // --- STEP 2: Weekly Plan ---
    // This will now fail fast if the backend hasn't done its job yet, preventing the CORS crash.
    const weeklyRow = await ensureWeeklyPlan(supabase, activeMonthly, today);
    
    // --- STEP 3: Create Daily Workout Shell ---
    setStatusMessage("Creating daily entry...");
    const focusList = effectiveFocusAreas.length > 0 ? effectiveFocusAreas : safeParse(activeMonthly.focus_areas, []);
    
    const { data: workoutRow, error: workoutError } = await supabase
      .from("skill_workouts")
      .insert({
        athlete_id: userId,
        program_id: program.id,
        workout_date: today,
        duration_minutes: effectiveWorkoutLength,
        focus: focusList.join(", "),
        ai_generated: true,
      })
      .select()
      .single();

    if (workoutError) throw new Error(`Daily Row Error: ${workoutError.message}`);

    // --- STEP 4: Gather Context ---
    const sevenDaysAgo = dayjs().subtract(6, "day").startOf("day").toISOString();
    const { data: recentDrills } = await supabase
      .from("workout_drills")
      .select("*")
      .gte("created_at", sevenDaysAgo);

    const previousScores = (recentDrills || []).reduce((acc, d) => {
      acc[d.drill_name] = d.duration_minutes;
      return acc;
    }, {});

    // --- STEP 5: Generate Drills (The ONLY Edge Function call) ---
    setStatusMessage("AI is designing your drills...");

    // FIX: Manually get session to handle Auth correctly
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) throw new Error("You must be logged in.");
    
    const payload = {
      user_current_scores: safeParse(activeMonthly.user_skill_scores),
      program_scores: safeParse(activeMonthly.program_skill_scores),
      focus_areas: focusList,
      previous_scores: previousScores,
      weekly_row_data: weeklyRow,
      start_date: today,
      current_age: userGrade || 11,
      user_grade: userGrade || 11,
      target_age: program.player_peaking || 18,
      sport: program.sport || "basketball",
      season_phase: seasonGuidance.phase,
      season_guidance: seasonGuidance.emphasis,
      workout_intensity_target: seasonGuidance.intensity,
      workout_length_recommendation: seasonGuidance.recommendedWorkoutLength,
      baseball_focus_domain: baseballFocusDomain || "all",
      swot_priorities: swotPriorityPayload || null,
      swot_skill_categories: swotPriorityPayload?.skill_categories || null,
      season_priorities: seasonPriorities,
      completed_priority_items: [],
      season_priority_focus_items: swotPriorityFocus,
      priority_prompt_directives: priorityPromptDirectives,
      workout_length: effectiveWorkoutLength,
    };

    // FIX: Using 'fetch' instead of 'invoke' to bypass CORS issues
    const res = await fetch(
      "https://kzklgxnbtquhjfywtnsz.functions.supabase.co/generateSkillWorkouts",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}` // Explicit valid token
        },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
        const errText = await res.text();
        throw new Error(`AI Generation Failed: ${res.status} ${errText}`);
    }

    const data = await res.json();

    // --- STEP 6: Parse and Insert ---
    setStatusMessage("Finalizing plan...");
    
    let rawContent = data.choices ? data.choices[0].message.content : data;
    if (typeof rawContent === 'string') {
        rawContent = rawContent.replace(/```json/g, "").replace(/```/g, "").trim();
        rawContent = JSON.parse(rawContent);
    }
    const workoutJSON = rawContent;

    const allDrills = [
      ...(workoutJSON.blocks["Block 1 — Review and Reinforce"] || []),
      ...(workoutJSON.blocks["Block 2 — Progression Drills"] || []),
      ...(workoutJSON.blocks["Block 3 — Game Situation Integration"] || []),
    ].map((d) => ({
      workout_id: workoutRow.id,
      drill_name: d.drill_name.trim(),
      description: d.description,
      duration: d.duration_minutes,
      sets: d.sets,
      reps: d.reps,
      created_at: dayjs().toISOString(),
    }));

    const drillsToInsert = allDrills.filter(
      (drill, index, arr) =>
        index === arr.findIndex((t) => t.drill_name === drill.drill_name)
    );

    const { error: insertError } = await supabase
      .from("workout_drills")
      .insert(drillsToInsert);

    if (insertError) throw insertError;

    return workoutJSON;

  } catch (err) {
    console.error("Generator Error:", err);
    throw err;
  }
};
